// Types
import {
  CampaignAppearanceType,
  CampaignMediaType,
  CampaignOfferAttention,
  CampaignOfferStatus,
  CampaignSmPlatform,
  CampaignStatus,
  CampaignTaskType,
  CampaignType,
  StreamType,
} from 'api/graphql-global-types';
import {
  GetMyCampaignOffers_getMyCampaignOffers_entities_campaign,
  GetMyCampaignOffers_getMyCampaignOffers_entities_submissions,
} from 'api/campaignV2/types/GetMyCampaignOffers';
import { CreateCampaignSubmissionFiles_createCampaignSubmissionFiles } from 'api/campaignV2/types/CreateCampaignSubmissionFiles';
import { SettingsFormInputs } from 'components/ManageAccountSettings/ManageBrandDealSettings/SettingsForm/SettingsForm';
import { MyStreams_myStreams_entities } from 'api/streams/types/MyStreams';
import {
  GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions,
  GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission,
  GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignRecordedAppearanceSubmission,
} from 'api/campaignV2/types/GetMyCampaignOfferByCampaignId';
// Helpers
import { getCounterOfferText } from 'helpers/counterOffer';

export const DATE_FORMAT = 'MM/DD/YYYY';

export const getContentTypeText = (
  contentTypes: CampaignTaskType[] | null
): string[] => {
  if (!contentTypes) return [];

  return contentTypes.map((type) => {
    switch (type) {
      case CampaignTaskType.IG_LIVE:
        return 'Instagram Live';
      case CampaignTaskType.IG_POST:
        return 'Instagram Post';
      case CampaignTaskType.IG_REEL:
        return 'Instagram Reel';
      case CampaignTaskType.IG_STORY:
        return 'Instagram Story';
      case CampaignTaskType.TT_VIDEO:
        return 'TikTok Video';
      case CampaignTaskType.TT_LIVE:
        return 'TikTok Live';
      case CampaignTaskType.TT_STORY:
        return 'TikTok Story';
      case CampaignTaskType.TW_TWEET:
        return 'X Tweet';
      case CampaignTaskType.TW_LIVE:
        return 'X Live';
      case CampaignTaskType.TW_VIDEO:
        return 'X Video';
      case CampaignTaskType.CONTENT:
        return 'Content';
      default:
        return 'Unknown';
    }
  });
};

export const getMediaContentTypeText = (
  mediaType: CampaignMediaType
): string => {
  switch (mediaType) {
    case CampaignMediaType.IMAGE:
      return 'Image';
    case CampaignMediaType.VIDEO:
      return 'Video';
    default:
      return 'Unknown';
  }
};

export const mapSocialMediaToLabel = (
  socialMediaOption: CampaignSmPlatform
): string => {
  switch (socialMediaOption) {
    case CampaignSmPlatform.IG:
      return 'Instagram';

    case CampaignSmPlatform.TT:
      return 'TikTok';

    case CampaignSmPlatform.TW:
      return 'X';
    default:
      return 'Millions';
  }
};

export const isPastCampaign = (
  campaign: GetMyCampaignOffers_getMyCampaignOffers_entities_campaign
) => {
  if (campaign.status === CampaignStatus.COMPLETED) {
    return true;
  }

  return false;
};

type ImageToUpload = {
  fields: string;
  url: string;
  key: string;
  type: string;
  file: File | string;
};

export const getImagesToUpload = (
  preSignedUrls: CreateCampaignSubmissionFiles_createCampaignSubmissionFiles[],
  images: FileWithPreview[]
): ImageToUpload[] => {
  const res: ImageToUpload[] = [];

  images.forEach((file, index) => {
    const preSignedUrl = preSignedUrls?.[index];

    res.push({
      fields: preSignedUrl?.fields || '',
      url: preSignedUrl?.url || '',
      key: preSignedUrl?.key,
      type: file?.type || '',
      file: file || '',
    });
  });

  return res;
};

export const uploadImages = async (
  preSignedUrls: CreateCampaignSubmissionFiles_createCampaignSubmissionFiles[],
  images: FileWithPreview[]
): Promise<string[]> => {
  const imagesToUpload = getImagesToUpload(preSignedUrls, images);

  for (const image of imagesToUpload) {
    const { fields, url, file, key, type } = image;
    const formData = new FormData();

    Object.entries(JSON.parse(fields)).forEach(([key, value]) => {
      formData.append(key, value as string);
    });

    formData.append('key', key);
    formData.append('file', file);
    formData.append('Content-Type', type);

    await fetch(url, {
      method: 'POST',
      body: formData,
    });
  }

  return imagesToUpload.map(({ key }) => key);
};

export const getStatus = (status: CampaignStatus | null): string => {
  switch (status) {
    case CampaignStatus.COMPLETED:
      return 'Completed';
    case CampaignStatus.DETAIL_FILLED:
      return 'Detail Filled';
    case CampaignStatus.PAID_AND_ACTIVE:
      return 'Paid and Active';
    default:
      return '';
  }
};

export const mapStatusToLabel = (status: CampaignOfferStatus) => {
  if (!status) return '';

  switch (status) {
    case CampaignOfferStatus.DECLINED:
      return 'Your application wasn’t selected for this opportunity.';
    case CampaignOfferStatus.PROPOSED:
      return 'Offer Sent';
    case CampaignOfferStatus.APPLIED:
      return 'Applied';
    case CampaignOfferStatus.ACCEPTED:
      return 'Your application has been accepted.';
    case CampaignOfferStatus.EXPIRED:
      return 'Expired';
    case CampaignOfferStatus.SCHEDULED:
      return 'Scheduled';
    case CampaignOfferStatus.PUBLISHED:
      return 'Published';
    case CampaignOfferStatus.COMPLETED:
      return 'Completed';
    default:
      return status;
  }
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
};

const excludedKeys = [
  'contactEmail',
  'contactPhoneNumberE164',
  'minFlatPrice',
  'minBookMePrice',
  'minSocialPrice',
  'minSocialSharePrice',
  'minSponsoredLivePrice',
  'isArticlesPrEnabled',
  'isAcceptingFreeProducts',
];

export const getDisabledCategories = (values: SettingsFormInputs): string[] => {
  return Object.keys(values).filter(
    (key) => !excludedKeys.includes(key) && values[key] === false
  );
};

export const mapOfferStatusToLabel = (
  status: CampaignOfferStatus,
  counterOfferAttention?: CampaignOfferAttention | null
) => {
  switch (status) {
    case CampaignOfferStatus.DECLINED:
      return 'Offer Declined';
    case CampaignOfferStatus.PROPOSED:
      return 'Offer Sent';
    case CampaignOfferStatus.INVITED:
      return 'Invited';
    case CampaignOfferStatus.SUBMITTED:
      return 'Submitted';
    case CampaignOfferStatus.DELIVERABLES_APPROVED:
      return 'Deliverables Approved';
    case CampaignOfferStatus.DELIVERABLES_REJECTED:
      return 'Deliverables Rejected';
    case CampaignOfferStatus.ACCEPTED:
      return 'Approved';
    case CampaignOfferStatus.APPLIED:
      return 'Applied';
    case CampaignOfferStatus.EXPIRED:
      return 'Expired';
    case CampaignOfferStatus.SCHEDULED:
      return 'Scheduled';
    case CampaignOfferStatus.PUBLISHED:
      return 'Published';
    case CampaignOfferStatus.COMPLETED:
      return 'Completed';
    case CampaignOfferStatus.COUNTER_OFFER_PENDING:
      return getCounterOfferText(counterOfferAttention);
    default:
      return status;
  }
};

export const getIsSponsoredAppearance = (
  stream: MyStreams_myStreams_entities
) => {
  return (
    stream.type === StreamType.SponsoredAppearanceLive ||
    stream.type === StreamType.SponsoredAppearanceRec ||
    stream.type === StreamType.SponsoredAppearanceDraft
  );
};

export const CAMPAIGN_OFFER_STATUSES = [
  {
    value: CampaignOfferStatus.SUBMITTED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.SUBMITTED),
  },
  {
    value: CampaignOfferStatus.DELIVERABLES_APPROVED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.DELIVERABLES_APPROVED),
  },
  {
    value: CampaignOfferStatus.DELIVERABLES_REJECTED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.DELIVERABLES_REJECTED),
  },
  {
    value: CampaignOfferStatus.ACCEPTED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.ACCEPTED),
  },
  {
    value: CampaignOfferStatus.APPLIED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.APPLIED),
  },
  {
    value: CampaignOfferStatus.DECLINED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.DECLINED),
  },
  {
    value: CampaignOfferStatus.EXPIRED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.EXPIRED),
  },
  {
    value: CampaignOfferStatus.INVITED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.INVITED),
  },
  {
    value: CampaignOfferStatus.COUNTER_OFFER_PENDING,
    label: mapOfferStatusToLabel(CampaignOfferStatus.COUNTER_OFFER_PENDING),
  },
  {
    value: CampaignOfferStatus.PROPOSED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.PROPOSED),
  },
  {
    value: CampaignOfferStatus.SCHEDULED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.SCHEDULED),
  },
  {
    value: CampaignOfferStatus.PUBLISHED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.PUBLISHED),
  },
  {
    value: CampaignOfferStatus.COMPLETED,
    label: mapOfferStatusToLabel(CampaignOfferStatus.COMPLETED),
  },
];

export const CAMPAIGN_TYPES = [
  {
    value: CampaignType.BookMe,
    label: 'Book me campaign',
  },
  {
    value: CampaignType.Custom,
    label: 'Custom Campaign',
  },
  {
    value: CampaignType.EventMarketing,
    label: 'Event Marketing',
  },
  {
    value: CampaignType.Social,
    label: 'Social Campaign',
  },
  {
    value: CampaignType.SponsoredAppearance,
    label: 'Sponsored appearance',
  },
  {
    value: CampaignType.SocialShare,
    label: 'Viral Share Campaign',
  },
  {
    value: CampaignType.Ugc,
    label: 'UGC Campaign',
  },
];

export const mapCampaignTypeTypeToLabel = (type: CampaignType) => {
  if (!type) return '';

  switch (type) {
    case CampaignType.BookMe:
      return 'Book me Campaign';
    // Direct Booking deals are presented to talent as custom campaigns
    case CampaignType.Custom:
    case CampaignType.DirectBooking:
      return 'Custom Campaign';
    case CampaignType.EventMarketing:
      return 'Event Marketing';
    case CampaignType.Social:
      return 'Social Campaign';
    case CampaignType.SponsoredAppearance:
      return 'Sponsored appearance';
    case CampaignType.SocialShare:
      return 'Viral Share Campaign';
    case CampaignType.SocialBoost:
      return 'Social Boost Campaign';
    case CampaignType.Ugc:
      return 'UGC Campaign';

    default:
      return type;
  }
};

export const getIsRecordedAppearance = (
  submissions:
    | GetMyCampaignOffers_getMyCampaignOffers_entities_submissions[]
    | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions[]
    | null
) => {
  if (!submissions) {
    return null;
  }

  return submissions[0]?.__typename === 'CampaignRecordedAppearanceSubmission';
};

export const getLiveAppearanceSubmission = (
  submissions:
    | GetMyCampaignOffers_getMyCampaignOffers_entities_submissions[]
    | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions[]
    | null
) => {
  if (!submissions) {
    return null;
  }

  return submissions[0] as GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission;
};

export const getRecordedAppearanceSubmission = (
  submissions:
    | GetMyCampaignOffers_getMyCampaignOffers_entities_submissions[]
    | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions[]
    | null
) => {
  if (!submissions) {
    return null;
  }

  return submissions[0] as GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignRecordedAppearanceSubmission;
};

export const isOnlyContentDeliverables = (
  campaignType?: CampaignType | null
) => {
  if (!campaignType) return false;

  return campaignType === CampaignType.Ugc;
};

export const isSocialLikeCampaign = (
  campaignType?: CampaignType | null
): boolean => {
  if (!campaignType) return false;

  return [
    CampaignType.Social,
    CampaignType.Custom,
    CampaignType.DirectBooking,
  ].includes(campaignType);
};

// Direct Booking is a privately booked deal with a single talent. It reuses the
// Custom campaign brief, deliverables and screens — only the invited actions differ.
export const isCustomLikeCampaign = (
  campaignType?: CampaignType | null
): boolean => {
  if (!campaignType) return false;

  return [CampaignType.Custom, CampaignType.DirectBooking].includes(
    campaignType
  );
};

export const isV3DeliverableCampaign = (
  campaignType?: CampaignType | null
): boolean => {
  if (!campaignType) return false;

  return [
    CampaignType.Social,
    CampaignType.Custom,
    CampaignType.Ugc,
    CampaignType.DirectBooking,
  ].includes(campaignType);
};

export const mapAppearanceTypeToLabel = (
  appearanceType?: CampaignAppearanceType | null
): string => {
  switch (appearanceType) {
    case CampaignAppearanceType.Photoshoot:
      return 'On-site Photoshoot';
    case CampaignAppearanceType.Speaking:
      return 'Speaking';
    case CampaignAppearanceType.MeetAndGreet:
      return 'Meet & Greet';
    case CampaignAppearanceType.Signing:
      return 'Signing';
    case CampaignAppearanceType.EventAttendance:
      return 'Event Attendance';
    case CampaignAppearanceType.OnSiteFilming:
      return 'On-site Filming';
    case CampaignAppearanceType.Other:
      return 'Other';
    default:
      return 'Appearance';
  }
};
